import { latinToGreekLookAlikes } from "./addressIdentityMap.generated";

/**
 * The browser half of the address identity fold
 * (address-normalization/FR-002, /FR-010).
 *
 * It is a deliberate second implementation of a rule the server also owns: the
 * combobox has to narrow the list as the operator types, and only the server can
 * refuse a duplicate. Drift between the two is the hazard, so the CHARACTER TABLE
 * is generated from the Go map (`addressIdentityMap.generated.ts`) and the
 * surrounding algorithm is pinned by the same golden vectors the Go tests use
 * (`analytics/public-protocol/testdata/address-identity-vectors.json`).
 *
 * That pins the RULE, not every input. A brute-force sweep of the code-point
 * space found 143 code points where the two folds still disagree — U+019B,
 * U+0897, the U+1ACF–U+1ADD combining marks and similar. The cause is outside
 * this feature: Node's ICU and Go's `unicode` tables are built from different
 * Unicode versions, so a mark or case mapping added in a newer revision is known
 * to one side and not the other. All 143 are exotic combining marks or recent
 * case-mapping additions, unreachable from Greek or Latin address text, and the
 * server's unique index on the normalized key stays authoritative either way —
 * the browser fold only narrows a list, it never decides identity. So the skew
 * cannot mint a second building; it could at worst fail to grey out a suggestion
 * for an address nobody can type.
 */

/**
 * Combining marks — what NFD splits an accent into — and format characters (the
 * BOM, zero-width joiners, soft hyphen, bidi controls). Both are dropped, and for
 * the same reason: they are INVISIBLE to the operator, so letting them
 * distinguish two identities means a pasted BOM mints a second building with no
 * duplicate warning from either side, splitting the monthly fee.
 */
const INVISIBLE_CHARACTERS = /[\p{Mn}\p{Cf}]/gu;

/**
 * The whitespace the fold collapses, written out rather than taken from the
 * runtime. `\s` is NOT this set and `String.trim` is not either: `\s` and `trim`
 * treat the BOM (U+FEFF) as space and ignore NEL (U+0085), while Go's
 * `unicode.IsSpace` — which `strings.Fields` uses, and which decides what the
 * SERVER stores — does the exact opposite. Either disagreement lets the browser
 * call two spellings one building while the server mints a second one, which is
 * the fee split this feature exists to stop. This class is Unicode White_Space
 * plus the ASCII controls, i.e. `unicode.IsSpace` exactly.
 */
const GO_WHITESPACE = /[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]/u;

/**
 * upperCaseRuneWise upper-cases ONE CODE POINT AT A TIME, as Go's
 * `unicode.ToUpper` does. `String.toUpperCase` applies Unicode FULL case mapping,
 * which expands a code point into several — `"ß"` to `"SS"`, the `"ﬁ"` ligature to
 * `"FI"` — where Go, having no simple upper-case mapping for those, leaves them
 * alone. A code point whose upper-case form is not itself a single code point is
 * therefore kept as it is, which reproduces Go's simple mapping over every code
 * point the two runtimes' Unicode tables agree on.
 */
function upperCaseRuneWise(value: string): string {
    return Array.from(value, (character) => {
        const upper = character.toUpperCase();
        return Array.from(upper).length === 1 ? upper : character;
    }).join("");
}

/**
 * foldAddressText reduces one address component (or a whole search query) to its
 * comparison form, in the same order as the Go `fold`:
 *
 *  1. trim and collapse internal runs of whitespace,
 *  2. decompose to NFD and drop the combining marks, which strips the Greek tonos
 *     (upper-casing alone does NOT — `"ά".toUpperCase()` is `"Ά"`), and drop the
 *     invisible format characters along with them,
 *  3. upper-case rune-wise, which also folds final sigma `ς` onto `Σ`,
 *  4. map the Latin look-alikes onto their Greek letters.
 *
 * It stops there on purpose. Digits, hyphens and letter suffixes survive, so
 * `16Α` stays distinct from `16` and `34-36` from `34`: an over-eager fold that
 * merged adjacent buildings would mis-bill exactly as badly as no fold at all.
 */
export function foldAddressText(value: string): string {
    const collapsed = value
        .split(GO_WHITESPACE)
        .filter((part) => part.length > 0)
        .join(" ");
    const unaccented = collapsed.normalize("NFD").replace(INVISIBLE_CHARACTERS, "");
    const upper = upperCaseRuneWise(unaccented);
    return Array.from(upper, (character) => latinToGreekLookAlikes.get(character) ?? character).join("");
}

/** Separator between the three identity components. Mirrors the Go constant. */
const IDENTITY_SEPARATOR = "|";

/**
 * addressIdentity is the comparison form of a whole building: two addresses with
 * the same identity are the same building and cannot both exist.
 */
export function addressIdentity(street: string, number: string, postalCode: string): string {
    return [street, number, postalCode].map(foldAddressText).join(IDENTITY_SEPARATOR);
}

/**
 * matchesAddressSearch answers "would this option be offered for this query?" —
 * the cmdk filter predicate behind FR-010. Both sides are folded, so a Latin `K`
 * or a dropped accent still finds the building.
 */
export function matchesAddressSearch(optionLabel: string, query: string): boolean {
    return foldAddressText(optionLabel).includes(foldAddressText(query));
}
